import type { Translation } from '@/core/types';
import { LanguageDetector } from './language-detector';

export class TranslationManager {
  private translations: Map<string, Translation> = new Map();
  private currentLanguage: string = 'en';
  private fallbackLanguage: string = 'en';

  constructor(customTranslations?: Record<string, Record<string, string>>) {
    this.loadDefaultTranslations();

    if (customTranslations) {
      this.mergeCustomTranslations(customTranslations);
    }

    this.currentLanguage = LanguageDetector.detectLanguage();
  }

  public getTranslation(language?: string): Translation {
    const lang = language || this.currentLanguage;
    const translation = this.translations.get(lang);

    if (translation) {
      return translation;
    }

    const fallback = this.translations.get(this.fallbackLanguage);
    if (fallback) {
      console.warn(
        `Translation for '${lang}' not found, using fallback '${this.fallbackLanguage}'`
      );
      return fallback;
    }

    throw new Error(
      `No translation found for '${lang}' and fallback '${this.fallbackLanguage}'`
    );
  }

  public getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  public setLanguage(language: string): Translation {
    if (LanguageDetector.isSupported(language)) {
      this.currentLanguage = language;
      LanguageDetector.setLanguage(language);
      return this.getTranslation(language);
    }

    throw new Error(`Unsupported language: ${language}`);
  }

  public getSupportedLanguages(): Array<{ code: string; name: string }> {
    return LanguageDetector.getSupportedLanguages().map((code) => ({
      code,
      name: LanguageDetector.getLanguageName(code),
    }));
  }

  public addTranslation(language: string, translation: Translation): void {
    this.translations.set(language, translation);
  }

  public updateTranslation(
    language: string,
    updates: Partial<Translation>
  ): void {
    const existing = this.translations.get(language);
    if (existing) {
      this.translations.set(language, this.deepMerge(existing, updates));
    }
  }

  private loadDefaultTranslations(): void {
    const defaultTranslations = this.getDefaultTranslations();

    Object.entries(defaultTranslations).forEach(([lang, translation]) => {
      this.translations.set(lang, translation);
    });
  }

  private mergeCustomTranslations(
    customTranslations: Record<string, Record<string, string>>
  ): void {
    Object.entries(customTranslations).forEach(([lang, translations]) => {
      const existing = this.translations.get(lang);
      if (existing) {
        const merged = this.mergeTranslationStrings(existing, translations);
        this.translations.set(lang, merged);
      } else {
        console.warn(
          `Custom translation for unsupported language '${lang}' ignored`
        );
      }
    });
  }

  private mergeTranslationStrings(
    base: Translation,
    custom: Record<string, string>
  ): Translation {
    const result = { ...base };

    Object.entries(custom).forEach(([key, value]) => {
      const keyParts = key.split('.');
      let current: any = result;

      for (let i = 0; i < keyParts.length - 1; i++) {
        if (!current[keyParts[i]]) {
          current[keyParts[i]] = {};
        }
        current = current[keyParts[i]];
      }

      current[keyParts[keyParts.length - 1]] = value;
    });

    return result;
  }

  private deepMerge(target: any, source: any): any {
    const result = { ...target };

    Object.keys(source).forEach((key) => {
      if (
        source[key] &&
        typeof source[key] === 'object' &&
        !Array.isArray(source[key])
      ) {
        result[key] = this.deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    });

    return result;
  }

  private getDefaultTranslations(): Record<string, Translation> {
    return {
      en: {
        banner: {
          title: 'We use cookies',
          description:
            'We use cookies and similar technologies to provide the best experience on our website. Some are necessary for functionality, while others help us improve and personalize your experience.',
          acceptAll: 'Accept All',
          rejectAll: 'Reject All',
          customize: 'Customize',
          moreInfo: 'More Info',
        },
        modal: {
          title: 'Privacy Preferences',
          description:
            'Choose which cookies you want to accept. You can change these settings at any time.',
          save: 'Save Preferences',
          close: 'Close',
          acceptAll: 'Accept All',
          rejectAll: 'Reject All',
        },
        categories: {
          essential: {
            name: 'Essential',
            description:
              'These cookies are necessary for the website to function and cannot be disabled.',
          },
          analytics: {
            name: 'Analytics',
            description:
              'These cookies help us understand how visitors use our website.',
          },
          advertising: {
            name: 'Advertising',
            description:
              'These cookies are used to show you relevant advertisements.',
          },
          functional: {
            name: 'Functional',
            description:
              'These cookies enable enhanced functionality and personalization.',
          },
          personalization: {
            name: 'Personalization',
            description:
              'These cookies help us remember your preferences and settings.',
          },
        },
        footer: {
          privacySettings: 'Privacy Settings',
          doNotSell: 'Do Not Sell My Personal Information',
        },
      },
      es: {
        banner: {
          title: 'Usamos cookies',
          description:
            'Utilizamos cookies y tecnologías similares para brindar la mejor experiencia en nuestro sitio web. Algunas son necesarias para el funcionamiento, mientras que otras nos ayudan a mejorar y personalizar su experiencia.',
          acceptAll: 'Aceptar Todo',
          rejectAll: 'Rechazar Todo',
          customize: 'Personalizar',
          moreInfo: 'Más Información',
        },
        modal: {
          title: 'Preferencias de Privacidad',
          description:
            'Elija qué cookies desea aceptar. Puede cambiar esta configuración en cualquier momento.',
          save: 'Guardar Preferencias',
          close: 'Cerrar',
          acceptAll: 'Aceptar Todo',
          rejectAll: 'Rechazar Todo',
        },
        categories: {
          essential: {
            name: 'Esenciales',
            description:
              'Estas cookies son necesarias para que el sitio web funcione y no se pueden desactivar.',
          },
          analytics: {
            name: 'Analíticas',
            description:
              'Estas cookies nos ayudan a entender cómo los visitantes usan nuestro sitio web.',
          },
          advertising: {
            name: 'Publicitarias',
            description:
              'Estas cookies se utilizan para mostrarle anuncios relevantes.',
          },
          functional: {
            name: 'Funcionales',
            description:
              'Estas cookies permiten funcionalidades mejoradas y personalización.',
          },
          personalization: {
            name: 'Personalización',
            description:
              'Estas cookies nos ayudan a recordar sus preferencias y configuraciones.',
          },
        },
        footer: {
          privacySettings: 'Configuración de Privacidad',
          doNotSell: 'No Vender Mi Información Personal',
        },
      },
      fr: {
        banner: {
          title: 'Nous utilisons des cookies',
          description:
            "Nous utilisons des cookies et des technologies similaires pour offrir la meilleure expérience sur notre site web. Certains sont nécessaires au fonctionnement, tandis que d'autres nous aident à améliorer et personnaliser votre expérience.",
          acceptAll: 'Tout Accepter',
          rejectAll: 'Tout Refuser',
          customize: 'Personnaliser',
          moreInfo: "Plus d'Informations",
        },
        modal: {
          title: 'Préférences de Confidentialité',
          description:
            'Choisissez quels cookies vous souhaitez accepter. Vous pouvez modifier ces paramètres à tout moment.',
          save: 'Enregistrer les Préférences',
          close: 'Fermer',
          acceptAll: 'Tout Accepter',
          rejectAll: 'Tout Refuser',
        },
        categories: {
          essential: {
            name: 'Essentiels',
            description:
              'Ces cookies sont nécessaires au fonctionnement du site web et ne peuvent pas être désactivés.',
          },
          analytics: {
            name: 'Analytiques',
            description:
              'Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site web.',
          },
          advertising: {
            name: 'Publicitaires',
            description:
              'Ces cookies sont utilisés pour vous montrer des publicités pertinentes.',
          },
          functional: {
            name: 'Fonctionnels',
            description:
              'Ces cookies permettent des fonctionnalités améliorées et la personnalisation.',
          },
          personalization: {
            name: 'Personnalisation',
            description:
              'Ces cookies nous aident à mémoriser vos préférences et paramètres.',
          },
        },
        footer: {
          privacySettings: 'Paramètres de Confidentialité',
          doNotSell: 'Ne Pas Vendre Mes Informations Personnelles',
        },
      },
      de: {
        banner: {
          title: 'Wir verwenden Cookies',
          description:
            'Wir verwenden Cookies und ähnliche Technologien, um die beste Erfahrung auf unserer Website zu bieten. Einige sind für die Funktionalität notwendig, während andere uns helfen, Ihre Erfahrung zu verbessern und zu personalisieren.',
          acceptAll: 'Alle Akzeptieren',
          rejectAll: 'Alle Ablehnen',
          customize: 'Anpassen',
          moreInfo: 'Mehr Informationen',
        },
        modal: {
          title: 'Datenschutz-Einstellungen',
          description:
            'Wählen Sie, welche Cookies Sie akzeptieren möchten. Sie können diese Einstellungen jederzeit ändern.',
          save: 'Einstellungen Speichern',
          close: 'Schließen',
          acceptAll: 'Alle Akzeptieren',
          rejectAll: 'Alle Ablehnen',
        },
        categories: {
          essential: {
            name: 'Erforderlich',
            description:
              'Diese Cookies sind für die Funktion der Website erforderlich und können nicht deaktiviert werden.',
          },
          analytics: {
            name: 'Analyse',
            description:
              'Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen.',
          },
          advertising: {
            name: 'Werbung',
            description:
              'Diese Cookies werden verwendet, um Ihnen relevante Werbung zu zeigen.',
          },
          functional: {
            name: 'Funktional',
            description:
              'Diese Cookies ermöglichen erweiterte Funktionalität und Personalisierung.',
          },
          personalization: {
            name: 'Personalisierung',
            description:
              'Diese Cookies helfen uns, Ihre Einstellungen und Präferenzen zu speichern.',
          },
        },
        footer: {
          privacySettings: 'Datenschutz-Einstellungen',
          doNotSell: 'Meine persönlichen Daten nicht verkaufen',
        },
      },
      it: {
        banner: {
          title: 'Utilizziamo i cookie',
          description:
            'Utilizziamo cookie e tecnologie simili per fornire la migliore esperienza sul nostro sito web. Alcuni sono necessari per il funzionamento, mentre altri ci aiutano a migliorare e personalizzare la vostra esperienza.',
          acceptAll: 'Accetta Tutto',
          rejectAll: 'Rifiuta Tutto',
          customize: 'Personalizza',
          moreInfo: 'Maggiori Informazioni',
        },
        modal: {
          title: 'Preferenze sulla Privacy',
          description:
            'Scegli quali cookie desideri accettare. Puoi modificare queste impostazioni in qualsiasi momento.',
          save: 'Salva Preferenze',
          close: 'Chiudi',
          acceptAll: 'Accetta Tutto',
          rejectAll: 'Rifiuta Tutto',
        },
        categories: {
          essential: {
            name: 'Essenziali',
            description:
              'Questi cookie sono necessari per il funzionamento del sito web e non possono essere disabilitati.',
          },
          analytics: {
            name: 'Analitici',
            description:
              'Questi cookie ci aiutano a capire come i visitatori utilizzano il nostro sito web.',
          },
          advertising: {
            name: 'Pubblicitari',
            description:
              'Questi cookie sono utilizzati per mostrarvi pubblicità pertinenti.',
          },
          functional: {
            name: 'Funzionali',
            description:
              'Questi cookie abilitano funzionalità avanzate e personalizzazione.',
          },
          personalization: {
            name: 'Personalizzazione',
            description:
              'Questi cookie ci aiutano a ricordare le vostre preferenze e impostazioni.',
          },
        },
        footer: {
          privacySettings: 'Impostazioni Privacy',
          doNotSell: 'Non Vendere le Mie Informazioni Personali',
        },
      },
      pt: {
        banner: {
          title: 'Utilizamos cookies',
          description:
            'Utilizamos cookies e tecnologias similares para fornecer a melhor experiência no nosso site. Alguns são necessários para funcionalidade, enquanto outros nos ajudam a melhorar e personalizar sua experiência.',
          acceptAll: 'Aceitar Todos',
          rejectAll: 'Rejeitar Todos',
          customize: 'Personalizar',
          moreInfo: 'Mais Informações',
        },
        modal: {
          title: 'Preferências de Privacidade',
          description:
            'Escolha quais cookies deseja aceitar. Pode alterar estas configurações a qualquer momento.',
          save: 'Guardar Preferências',
          close: 'Fechar',
          acceptAll: 'Aceitar Todos',
          rejectAll: 'Rejeitar Todos',
        },
        categories: {
          essential: {
            name: 'Essenciais',
            description:
              'Estes cookies são necessários para o funcionamento do site e não podem ser desabilitados.',
          },
          analytics: {
            name: 'Analíticos',
            description:
              'Estes cookies ajudam-nos a compreender como os visitantes usam o nosso site.',
          },
          advertising: {
            name: 'Publicitários',
            description:
              'Estes cookies são usados para mostrar anúncios relevantes.',
          },
          functional: {
            name: 'Funcionais',
            description:
              'Estes cookies permitem funcionalidades melhoradas e personalização.',
          },
          personalization: {
            name: 'Personalização',
            description:
              'Estes cookies ajudam-nos a lembrar suas preferências e configurações.',
          },
        },
        footer: {
          privacySettings: 'Configurações de Privacidade',
          doNotSell: 'Não Vender Minhas Informações Pessoais',
        },
      },
    };
  }
}
